<template>
  <header class="ed-header">
    <button type="button" class="ed-header__back" title="Voltar" aria-label="Voltar" @click="$emit('back')">
      <i class="fal fa-arrow-left"></i>
    </button>

    <div class="ed-header__main">
      <div class="ed-header__eyebrow">
        Editar campanha <span class="ed-header__beta">beta</span>
      </div>
      <template v-if="header">
        <h1 class="ed-header__title">{{ header.name }}</h1>
        <div class="ed-header__meta">
          <span class="ed-chip" :class="'ed-chip--' + phaseTone">{{ phaseName(header.campaignPhaseId) }}</span>
          <span class="ed-chip ed-chip--neutral">{{ campaignTypeName(header.campaignTypeId) }}</span>
          <span v-if="header.companyName" class="ed-header__company">{{ header.companyName }}</span>
          <span v-if="updatedAtText" class="ed-header__updated">Salvo no novo editor em {{ updatedAtText }}</span>
        </div>
      </template>
      <div v-else class="ed-header__loading">Carregando campanha…</div>
    </div>

    <div v-if="header" class="ed-header__actions">
      <v-btn v-if="publicUrl" outline round color="primary" class="ed-header__btn" :href="publicUrl" target="_blank" rel="noopener">
        <i class="fal fa-external-link mr-2"></i>Ver campanha
      </v-btn>
      <v-btn flat round class="ed-header__btn" :to="oldEditorRoute">Editor antigo</v-btn>
    </div>
  </header>
</template>

<script>
import { Routes } from "@/scripts/models/enums/routes.enum.js";
import { phaseName, campaignTypeName, campaignPublicUrl } from "./editorLabels";

// 1 nova, 2 em andamento, 3 pausada, 4 encerrada, 5 cancelada (mesma ordem de phaseName).
const PHASE_TONES = Object.freeze({ 1: "info", 2: "success", 3: "warning", 4: "neutral", 5: "danger" });

export default {
  name: "EditorHeader",
  props: {
    header: { type: Object, default: null },
    campaignId: { type: Number, required: true },
  },
  computed: {
    phaseTone() {
      return PHASE_TONES[this.header.campaignPhaseId] || "neutral";
    },
    publicUrl() {
      return campaignPublicUrl(this.header.subdomain);
    },
    updatedAtText() {
      if (!this.header.updatedAt) return null;
      // O back grava em UTC sem fuso; força a interpretação como UTC.
      const raw = String(this.header.updatedAt);
      const date = new Date(/Z|[+-]\d\d:\d\d$/.test(raw) ? raw : raw + "Z");
      return isNaN(date.getTime()) ? null : date.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
    },
    oldEditorRoute() {
      return { name: Routes.app.CampaignEdit, query: { campaignId: this.campaignId } };
    },
  },
  methods: {
    phaseName,
    campaignTypeName,
  },
};
</script>

<style scoped>
.ed-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 24px;
  background: var(--ced-surface, #fff);
  border: 1px solid var(--ced-border, #e3e5ec);
  border-radius: var(--ced-radius, 10px);
}
.ed-header__back {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid var(--ced-border, #e3e5ec);
  background: transparent;
  color: var(--ced-muted, #80849a);
  font-size: 16px;
  cursor: pointer;
}
.ed-header__back:hover {
  color: var(--ced-primary, #02acb4);
  border-color: var(--ced-primary, #02acb4);
}
.ed-header__main {
  flex: 1;
  min-width: 0;
}
.ed-header__eyebrow {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ced-muted, #80849a);
}
.ed-header__beta {
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 4px;
  background: var(--ced-primary-soft, #e6f7f8);
  color: var(--ced-primary, #02acb4);
  letter-spacing: 0.04em;
}
.ed-header__title {
  font-size: 24px;
  font-weight: 700;
  line-height: 1.25;
  color: var(--ced-text, #4c4d4f);
  margin: 2px 0 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ed-header__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  font-size: 13px;
  color: var(--ced-muted, #80849a);
}
.ed-header__loading {
  padding: 8px 0;
  color: var(--ced-muted, #80849a);
}
.ed-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}
.ed-chip--success { background: #ebf6dc; color: #4d7a12; }
.ed-chip--info { background: #e6f7f8; color: #01848a; }
.ed-chip--warning { background: #fdf2dc; color: #9a6a06; }
.ed-chip--danger { background: #fde7e7; color: #b42318; }
.ed-chip--neutral { background: #f1f2f5; color: #5f6377; }
.ed-header__actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
}
.ed-header__btn {
  text-transform: none;
  font-weight: 600;
}
@media (max-width: 960px) {
  .ed-header {
    flex-wrap: wrap;
    align-items: flex-start;
    padding: 16px;
  }
  .ed-header__title {
    white-space: normal;
  }
  .ed-header__actions {
    width: 100%;
  }
}
</style>
